import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Credentials } from "../types";

/**
 * Context value for Fusion communication
 */
interface FusionContextValue {
  credentials: Credentials | null;
  bridgePort: MessagePort | null;
}

const FusionContext = createContext<FusionContextValue | null>(null);

/**
 * Provider component that handles communication with the Fusion parent frame.
 */
export const FusionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  const [bridgePort, setBridgePort] = useState<MessagePort | null>(null);

  const handleMessage = useCallback((event: MessageEvent) => {
    // Security: Only accept messages from our parent window
    if (event.source !== window.parent) {
      return;
    }

    // Handle bridge port message (separate from credentials)
    if (event.data?.type === "EOS_BRIDGE_PORT") {
      const port = event.ports?.[0] ?? null;
      if (port) {
        setBridgePort((prev) => prev ?? port); // Only set if not already set
      }
      return;
    }

    // Handle credentials from Fusion
    if (event.data?.token && event.data?.project && event.data?.baseUrl) {
      setCredentials((prev) => {
        const newCreds = {
          token: event.data.token,
          project: event.data.project,
          baseUrl: event.data.baseUrl,
        };
        // Only update if values actually changed
        if (
          prev?.token === newCreds.token &&
          prev?.project === newCreds.project &&
          prev?.baseUrl === newCreds.baseUrl
        ) {
          return prev; // Return same reference to prevent re-renders
        }

        return newCreds;
      });
    }
  }, []);

  useEffect(() => {
    window.addEventListener("message", handleMessage);

    // Send app ready signal to parent window (Fusion)
    if (window.parent !== window) {
      window.parent.postMessage({ type: "APP_HOST_READY" }, "*");
    }

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [handleMessage]);

  const value = useMemo<FusionContextValue>(
    () => ({ credentials, bridgePort }),
    [credentials, bridgePort],
  );

  return (
    <FusionContext.Provider value={value}>{children}</FusionContext.Provider>
  );
};

/**
 * Hook to access credentials from the Fusion parent frame.
 * Must be used within a FusionProvider.
 */
export const useCredentials = (): { credentials: Credentials | null } => {
  const context = useContext(FusionContext);
  if (context === null) {
    throw new Error("useCredentials must be used within a FusionProvider");
  }
  return { credentials: context.credentials };
};

/**
 * Hook to access the bridge port for Comlink communication.
 * Must be used within a FusionProvider.
 *
 * This hook is idempotent - it always returns the same port reference.
 * Note: The port can only be transferred once (browser constraint).
 * Attempting to transfer it again will throw - consumer must handle this.
 */
export const useBridgePort = (): MessagePort | null => {
  const context = useContext(FusionContext);
  if (context === null) {
    throw new Error("useBridgePort must be used within a FusionProvider");
  }
  return context.bridgePort;
};
